import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Refresh,
  OpenInNew,
  Close,
  HelpOutline
} from "@mui/icons-material";
import { useTheme } from "../../context/ThemeContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import composioService from "../../services/composioService";
import customToolsService from "../../services/customToolsService";
import styles from "./AddToolDialog.module.scss";

const AddToolDialog = ({ open, onClose, editTool = null }) => {
  const { isDarkMode } = useTheme();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250",
      },
      secondary: {
        main: "#8b5cf6",
      },
    },
  });

  const [allTools, setAllTools] = useState([]); // Combined list of Composio apps and custom tools
  const [selectedTool, setSelectedTool] = useState("");
  const [selectedToolType, setSelectedToolType] = useState(""); // 'composio' or 'custom'
  const [selectedToolkitSlug, setSelectedToolkitSlug] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [actions, setActions] = useState([]);
  const [selectedActionDetails, setSelectedActionDetails] = useState(null);
  const [loadingApps, setLoadingApps] = useState(false);
  const [loadingActions, setLoadingActions] = useState(false);
  const [error, setError] = useState(null);
  const isEditModeRef = useRef(false);
  const [isEditModeLoading, setIsEditModeLoading] = useState(false);

  // Helper component for detailed tooltips - matching Rules book Dialog style
  const InfoTooltip = ({ title, content, children, placement = "top", maxWidth = 1500 }) => (
    <Tooltip
      title={
        <Box sx={{
          width: '100%',
          maxHeight: '400px',
          overflowY: 'auto',
          p: 1,
          boxSizing: "border-box",
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.5)',
            },
          },
        }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "white" }}
          >
            {title}
          </Typography>
          {typeof content === "string" ? (
            <Typography variant="body2" sx={{
              color: "rgba(255,255,255,0.9)",
              wordWrap: "break-word",
              overflowWrap: "break-word"
            }}>
              {content}
            </Typography>
          ) : (
            content
          )}
        </Box>
      }
      placement={placement}
      arrow
      enterDelay={300}
      leaveDelay={200}
      sx={{
        "& .MuiTooltip-tooltip": {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          fontSize: "0.875rem",
          maxWidth: `${maxWidth}px !important`,
          padding: "12px 16px",
          wordWrap: "break-word",
          overflowWrap: "break-word",
          whiteSpace: "normal",
          boxSizing: "border-box",
        },
        "& .MuiTooltip-arrow": {
          color: "rgba(0, 0, 0, 0.9)",
        },
      }}
    >
      {children}
    </Tooltip>
  );

  // Reset and fetch data when dialog opens
  useEffect(() => {
    if (open) {
      // Reset all form data
      setSelectedTool("");
      setSelectedToolkitSlug("");
      setSelectedAction("");
      setActions([]);
      setSelectedActionDetails(null);
      setError(null);

      fetchConnectedApps();
    }
  }, [open]);

  // Handle edit tool setup after dialog opens
  useEffect(() => {
    if (open && editTool) {
      isEditModeRef.current = true;
      setIsEditModeLoading(true);
      setSelectedTool(editTool.appName);
      const editType = editTool.toolType || 'composio';
      setSelectedToolType(editType);

      if (editType === 'composio') {
        // Toolkit-level binding: no action to select.
        setSelectedToolkitSlug(editTool.toolkitSlug || '');
        setIsEditModeLoading(false);
      } else if (editType === 'mcp_server') {
        setSelectedAction(editTool.actionName || '*');
        setIsEditModeLoading(false);
      } else {
        // For custom tools, use the customToolName if available
        const toolNameForActions = editTool.customToolName || editTool.appName;
        setTimeout(() => {
          fetchActions(toolNameForActions, editType).finally(() => {
            setIsEditModeLoading(false);
          });
        }, 100);
      }
    } else {
      isEditModeRef.current = false;
      setIsEditModeLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editTool]);

  const resolveSelectedToolRecord = (toolValue = selectedTool) =>
    allTools.find((t) => {
      if (t.toolType === 'custom' || t.toolType === 'mcp_server') {
        return t.name === toolValue;
      }
      return (t.app_name || t.name || t.appId || t.id) === toolValue;
    });

  const resolveComposioAppId = (tool) =>
    tool?.app_id || tool?.appId || tool?.id || '';

  // Fetch actions when a tool is selected (but not during edit setup)
  useEffect(() => {
    if (selectedTool && !isEditModeRef.current && selectedToolType) {
      if (selectedToolType === 'composio') {
        setSelectedAction("");
        setSelectedActionDetails(null);
        const tool = resolveSelectedToolRecord();
        const appId = resolveComposioAppId(tool);
        if (appId) fetchActions(appId, 'composio');
      } else if (selectedToolType === 'mcp_server') {
        setSelectedAction('*');
        setActions([{ name: '*', description: 'All available tools from this MCP server' }]);
      } else {
        // Find the tool to get the actual tool name for custom tools
        const tool = allTools.find(t => t.toolType === 'custom' && t.name === selectedTool);

        // For custom tools, use tool_name (e.g., 'smtp')
        const toolNameForActions = tool?.tool_name || selectedTool;
        fetchActions(toolNameForActions, selectedToolType);
      }
    } else if (!selectedTool) {
      setActions([]);
      setSelectedAction("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTool, selectedToolType, allTools]);

  // Set selected action when editing and actions are loaded
  useEffect(() => {
    if (editTool && actions.length > 0 && selectedTool === editTool.appName && !selectedAction) {
      // Find the action in the loaded actions
      const actionExists = actions.find(action =>
        (action.name || action.action_name || action.id) === editTool.actionName
      );
      if (actionExists) {
        setSelectedAction(editTool.actionName);
      } else {
        // Try again after a short delay in case actions are still loading
        setTimeout(() => {
          const retryActionExists = actions.find(action =>
            (action.name || action.action_name || action.id) === editTool.actionName
          );
          if (retryActionExists) {
            setSelectedAction(editTool.actionName);
          }
        }, 500);
      }
    }
  }, [editTool, actions, selectedTool, selectedAction]);

  const fetchConnectedApps = async () => {
    setLoadingApps(true);
    setError(null);
    try {
      const [composioResponse, customToolsResponse, userDefinedToolsBox] = await Promise.all([
        composioService.getConnectedApps(),
        customToolsService.getConfiguredTools().catch(err => {
          console.error('Error fetching configured custom tools:', err);
          return { configs: [], total: 0 };
        }),
        customToolsService.getCustomTools().catch(err => {
          console.error('Error fetching user defined tools:', err);
          return [];
        })
      ]);

      const apps = composioResponse.connections || composioResponse.connected_apps || [];

      // Filter only active/connected apps
      const activeApps = apps.filter(app => {
        const status = app.status || app.connection_status;
        return status === 'active' || status === 'connected';
      });

      const configuredTools = customToolsResponse.configs || [];
      const userDefinedToolsBoxRaw = userDefinedToolsBox || [];
      const userDefinedTools = Array.isArray(userDefinedToolsBoxRaw)
        ? userDefinedToolsBoxRaw
        : (userDefinedToolsBoxRaw.tools || []);
      const sharedToolItems = !Array.isArray(userDefinedToolsBoxRaw) && Array.isArray(userDefinedToolsBoxRaw.shared)
        ? userDefinedToolsBoxRaw.shared
        : [];

      console.log(`Fetched apps: Composio=${activeApps.length}, Configured=${configuredTools.length}, UserDefined=${userDefinedTools.length}`);
      // Combine Composio tools and configured custom tools
      const composioTools = activeApps.map(app => ({
        ...app,
        toolType: 'composio',
        displayName: app.app_name || app.name || app.appId || app.id
      }));

      const customTools = configuredTools.map(config => ({
        ...config,
        toolType: 'custom',
        displayName: config.name, // User's friendly name
        name: config.name, // This will be used for display and matching
        tool_name: config.tool_name, // Keep the actual tool name (smtp)
        // Store the config data for later use
        configId: config.id,
        toolConfig: config.config
      }));

      const mcpIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" >
          <path d="m19.97,11.84c.66-.66,1.02-1.53,1.02-2.46s-.36-1.8-1.02-2.46l-.04-.04c-.66-.66-1.53-1.02-2.46-1.02-.17,0-.34.03-.51.05.02-.17.05-.33.05-.51,0-.93-.36-1.8-1.02-2.46-.66-.66-1.53-1.02-2.46-1.02s-1.8.36-2.46,1.02l-7.87,7.87c-.27.27-.27.71,0,.98s.71.27.98,0l7.87-7.87c.39-.39.92-.61,1.47-.61s1.08.22,1.47.61c.39.39.61.92.61,1.48s-.22,1.08-.61,1.48l-5.86,5.86-.08.08c-.27.27-.27.71,0,.98.14.14.31.2.49.2s.36-.07.49-.2l5.94-5.94c.39-.39.92-.61,1.48-.61s1.08.22,1.47.61l.04.04c.39.39.61.92.61,1.47s-.22,1.08-.61,1.48l-7.11,7.11c-.63.63-.63,1.66,0,2.29l1.46,1.46c.14.14.31.2.49.2s.36-.07.49-.2c.27-.27.27-.71,0-.98l-1.46-1.46c-.09-.09-.09-.24,0-.33l7.11-7.11Z" /><path d="m17.96,9.83c.27-.27.27-.71,0-.98-.27-.27-.71-.27-.98,0l-5.82,5.82c-.81.81-2.14.81-2.95,0-.81-.81-.81-2.14,0-2.95l5.82-5.82c.27-.27.27-.71,0-.98-.27-.27-.71-.27-.98,0l-5.82,5.82c-1.36,1.36-1.36,3.56,0,4.92.68.68,1.57,1.02,2.46,1.02s1.78-.34,2.46-1.02l5.82-5.82Z" />
        </svg>
      );

      // Map user defined tools
      const userCustomTools = userDefinedTools.map(tool => ({
        ...tool,
        toolType: tool.api_type === 'MCP' ? 'mcp_server' : 'custom',
        displayName: tool.name,
        name: tool.name,
        tool_name: tool.tool_key, // using tool_key as the identifier
        id: tool.id,
        icon: tool.api_type === 'MCP' ? mcpIcon : '🛠️' // Use custom SVG
      }));

      const sharedCustomTools = sharedToolItems.map((item) => {
        const tool = item.resource || {};
        return {
          ...tool,
          id: item.resource_id ?? tool.id,
          toolType: tool.api_type === 'MCP' ? 'mcp_server' : 'custom',
          displayName: tool.name,
          name: tool.name,
          tool_name: tool.tool_key,
          icon: tool.api_type === 'MCP' ? mcpIcon : '🛠️',
          __shared: true,
          __sharePermission: item.permission || 'read',
          __shareOwner: item.owner,
        };
      });

      const combined = [...composioTools, ...customTools, ...userCustomTools, ...sharedCustomTools];
      setAllTools(combined);

      if (combined.length === 0) {
        setError("No connected tools found. Please configure a tool first.");
      }
    } catch (err) {
      console.error("Error fetching connected apps:", err);
      setError("Failed to load connected apps. Please try again.");
    } finally {
      setLoadingApps(false);
    }
  };

  const fetchActions = async (appName, toolType = 'composio') => {
    setLoadingActions(true);
    setError(null);
    try {
      let actionsData = [];

      if (toolType === 'custom' || toolType === 'mcp_server') {
        const tool = allTools.find(t => (t.tool_name === appName || t.name === appName) && (t.toolType === 'custom' || t.toolType === 'mcp_server'));

        if (tool && tool.configId) {
          // Fetch custom tool actions (system configured)
          const response = await customToolsService.getToolActions(appName);
          actionsData = response || [];
        } else if (tool && tool.id) {
          // Fetch user defined custom tool actions
          const toolDef = await customToolsService.getCustomToolById(tool.id);
          // Actions are stored in the 'actions' array in the definition
          actionsData = toolDef.actions || [];

          if (actionsData.length === 0 && toolDef?.api_type === 'MCP') {
            try {
              const payload = {};
              if (toolDef.mcp_config?.transport === 'http') {
                payload.mcp_server_url = toolDef.mcp_server_url;
                if (toolDef.api_key) payload.api_key = toolDef.api_key;
                if (toolDef.mcp_config.http_headers) {
                  payload.mcp_config = { transport: 'http', http_headers: toolDef.mcp_config.http_headers };
                }
              } else if (toolDef.mcp_config?.transport === 'stdio') {
                payload.mcp_config = toolDef.mcp_config;
              }
              const liveTools = await customToolsService.fetchMcpTools(payload);

              if (liveTools && Array.isArray(liveTools)) {
                actionsData = liveTools.map(t => {
                  let params = t.parameters || [];
                  if (!Array.isArray(params) && typeof params === 'object' && params.properties) {
                    const properties = params.properties;
                    params = Object.keys(properties).map(key => ({
                      name: key,
                      type: properties[key].type || 'string',
                      description: properties[key].description || ''
                    }));
                  }
                  return { ...t, parameters: Array.isArray(params) ? params : [] };
                });
              }
            } catch (e) {
              console.error("Dynamic MCP Fetch Failed during AddToolDialog:", e);
            }
          }
        } else {
          console.error("Could not find tool ID for user custom tool:", appName);
          // Fallback if we have the ID directly as appName (unlikely but safe)
          const toolDef = await customToolsService.getCustomToolById(appName);
          actionsData = toolDef.actions || [];
        }
      } else {
        // Fetch Composio app actions
        const response = await composioService.getAppActions(appName);

        // Handle the new API response structure: tools array with function objects
        if (response.tools && Array.isArray(response.tools)) {
          // Extract function details from each tool
          actionsData = response.tools.map(tool => {
            if (tool.function) {
              return {
                name: tool.function.name,
                description: tool.function.description,
                parameters: tool.function.parameters,
                type: tool.type || 'function'
              };
            }
            return null;
          }).filter(Boolean); // Remove any null entries
        } else if (response.actions && Array.isArray(response.actions)) {
          // Fallback to old structure if it exists
          actionsData = response.actions;
        }
      }

      setActions(actionsData);

      if (actionsData.length === 0 && toolType !== 'composio') {
        setError(`No actions available for ${appName}`);
      } else if (toolType === 'composio') {
        setError(null);
      }
    } catch (err) {
      console.error(`Error fetching actions for ${appName}:`, err);
      if (toolType !== 'composio') {
        setError(`Failed to load actions for ${appName}`);
      }
      setActions([]);
    } finally {
      setLoadingActions(false);
    }
  };

  const handleToolChange = (event) => {
    const toolValue = event.target.value;
    setSelectedTool(toolValue);
    setSelectedAction(""); // Reset action when tool changes
    setSelectedToolkitSlug("");

    const tool = resolveSelectedToolRecord(toolValue);
    if (tool) {
      setSelectedToolType(tool.toolType);
      if (tool.toolType === 'mcp_server') {
        setSelectedAction("*");
      } else if (tool.toolType === 'composio') {
        // Capture toolkit slug for the new toolkit-only payload.
        // Priority: connection.toolkit_slug → app.key → lowercased app_name.
        const slug =
          tool.toolkit_slug ||
          tool.key ||
          (tool.app_name || tool.name || '').toString().toLowerCase();
        setSelectedToolkitSlug(slug);
      }
    }
  };

  const handleActionChange = (event) => {
    const actionName = event.target.value;
    setSelectedAction(actionName);

    // Find the action details
    const actionDetails = actions.find(
      (action) => (action.name || action.action_name || action.id) === actionName
    );
    setSelectedActionDetails(actionDetails || null);
  };

  const handleClose = () => {
    setSelectedTool("");
    setSelectedToolkitSlug("");
    setSelectedAction("");
    setActions([]);
    setSelectedActionDetails(null);
    setError(null);
    onClose(null); // Pass null to indicate cancellation
  };

  const handleConnect = () => {
    if (!selectedTool) return;

    const effectiveType = selectedToolType || 'composio';
    const isComposio = effectiveType === 'composio';

    // Composio rows bind to a toolkit; custom and MCP rows still pin an action.
    if (!isComposio && !selectedAction) return;

    // Find the full tool details from the connected list
    const toolDetails = allTools.find((tool) => {
      if (tool.toolType === 'custom' || tool.toolType === 'mcp_server') {
        return tool.name === selectedTool;
      } else {
        return (tool.app_name || tool.name || tool.appId || tool.id) === selectedTool;
      }
    });

    // For custom tools, use tool_name (string) as appId; for Composio, use their ID
    let appId;
    if (toolDetails?.toolType === 'custom' || toolDetails?.toolType === 'mcp_server') {
      appId = toolDetails.tool_name; // This is the tool_key
    } else {
      appId = (toolDetails?.app_id || toolDetails?.appId || toolDetails?.id || selectedTool);
    }

    const toolData = {
      id: editTool?.id || Date.now(),
      appId: appId,
      appName: selectedTool,
      toolType: toolDetails?.toolType || effectiveType,
      appLogo: toolDetails?.logo || editTool?.appLogo || null,
      icon: toolDetails?.icon || null,
    };

    if (isComposio) {
      const slug =
        selectedToolkitSlug ||
        toolDetails?.toolkit_slug ||
        toolDetails?.key ||
        (toolDetails?.app_name || toolDetails?.name || selectedTool || '').toString().toLowerCase();
      toolData.toolkitSlug = slug;
      toolData.displayName = selectedTool;
    } else {
      toolData.actionName = selectedAction;
      toolData.displayName = `${selectedTool}_${selectedAction}`.toUpperCase().replace(/\s+/g, '_');
      if (toolDetails?.toolType === 'custom' || toolDetails?.toolType === 'mcp_server') {
        toolData.customToolName = toolDetails.name;
        toolData.customToolKey = toolDetails.tool_name;
        if (!toolDetails.configId) {
          toolData.customToolId = toolDetails.id;
        } else {
          toolData.customToolConfigId = toolDetails.configId;
          toolData.customToolConfig = toolDetails.toolConfig;
        }
      }
    }

    onClose(toolData);
  };

  const isComposioTool = selectedToolType === 'composio';
  const connectDisabled =
    !selectedTool ||
    loadingApps ||
    (!isComposioTool && (!selectedAction || loadingActions || isEditModeLoading));

  const connectDisabledReason = (() => {
    if (loadingApps) return 'Loading connected tools…';
    if (!selectedTool) return 'Select a connected tool first';
    if (!isComposioTool && !selectedAction) return 'Select an action for this tool';
    if (!isComposioTool && (loadingActions || isEditModeLoading)) return 'Loading actions…';
    return '';
  })();

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        className={styles.dialog}
        PaperProps={{
          className: styles.dialogPaper,
          style: {
            overflow: 'visible',
            position: 'relative',
          },
        }}
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              overflow: 'visible',
            },
          },
          '& .MuiMenu-root': {
            '& .MuiPaper-root': {
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: '300px',
              overflowY: 'auto',
            },
          },
        }}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" className={styles.title}>
              {editTool ? 'Edit Tool' : 'Add Tool'}
            </Typography>
            <InfoTooltip
              title="Tool Configuration Guide"
              content={
                <Box>
                  <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                    <strong>How to configure tools:</strong>
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                    ✅ Pick a toolkit
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                    Choose from your connected applications. Only active/connected apps are shown. The agent picks the right action per request — describe the task in your instructions.
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                    ✅ Custom & MCP tools
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                    Custom tools still pin a specific action; MCP servers expose all of their actions automatically.
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                    💡 Parameters:
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                    • Add parameters in agent instructions or during conversation
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                    • (*) indicates mandatory fields
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small" sx={{ color: "#e36250" }}>
                <HelpOutline fontSize="small" />
              </IconButton>
            </InfoTooltip>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            className={styles.closeButton}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent} sx={{ paddingBottom: 1 }}>
          <Box className={styles.formContainer}>
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Tool Selection */}
            <Box className={styles.section}>
              <Typography variant="body2" className={styles.sectionLabel}>
                Tool <span className={styles.required}>*</span>
              </Typography>
              <Box className={styles.toolSelectContainer}>
                <FormControl
                  fullWidth
                  size="small"
                  className={styles.selectField}
                  disabled={loadingApps}
                >
                  <InputLabel>
                    {loadingApps ? "Loading apps..." : "Select Tool..."}
                  </InputLabel>
                  <Select
                    value={selectedTool}
                    onChange={handleToolChange}
                    label={loadingApps ? "Loading apps..." : "Select Tool..."}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    {loadingApps ? (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Loading connected tools...
                      </MenuItem>
                    ) : allTools.length === 0 ? (
                      <MenuItem disabled>
                        No connected tools found
                      </MenuItem>
                    ) : (
                      allTools.map((tool) => {
                        const isCustomTool = tool.toolType === 'custom' || tool.toolType === 'mcp_server';
                        // For custom tools, use the friendly name; for Composio, use app_name
                        const toolName = isCustomTool ? tool.name : (tool.app_name || tool.name || tool.appId || tool.id);
                        return (
                          <MenuItem key={`${tool.toolType}-${toolName}`} value={toolName}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isCustomTool ? (
                                <Box sx={{ fontSize: '1.2rem' }}>{tool.icon || '🔧'}</Box>
                              ) : tool.logo ? (
                                <Box
                                  component="img"
                                  src={tool.logo}
                                  alt={toolName}
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    objectFit: 'contain',
                                    borderRadius: 0.5
                                  }}
                                />
                              ) : null}
                              <Typography variant="body2">{toolName}</Typography>
                              {isCustomTool && (
                                <Chip
                                  label={tool.__shared ? `Shared · ${tool.__sharePermission}` : tool.toolType === 'mcp_server' ? 'MCP' : (!tool.toolConfig) ? 'Custom' : 'Configured'}
                                  size="small"
                                  sx={{
                                    ml: 'auto',
                                    height: 18,
                                    fontSize: '0.65rem'
                                  }}
                                />
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })
                    )}
                  </Select>
                </FormControl>
                <IconButton
                  size="small"
                  className={styles.refreshButton}
                  onClick={fetchConnectedApps}
                  disabled={loadingApps}
                >
                  {loadingApps ? <CircularProgress size={16} /> : <Refresh />}
                </IconButton>
              </Box>
            </Box>

            {/* Configure More Tools Link */}
            <Box className={styles.linkContainer}>
              <Typography
                variant="caption"
                className={styles.configureLink}
                onClick={() => window.open("/configure/tools", "_blank")}
              >
                Configure more tools
                <OpenInNew fontSize="inherit" />
              </Typography>
            </Box>

            {isComposioTool && selectedTool && (
              <Alert severity="info" className={styles.composioToolkitAlert}>
                <strong>{selectedTool}</strong> connects as a full toolkit for the agent to <em>use</em> during runs
                (send email, search inbox, etc.). To <em>start</em> the agent when app events happen, save the agent and
                add a <strong>Composio trigger</strong> from the agent header <strong>Triggers</strong> button or{' '}
                Configure → Triggers — pick the same connected account and an event type (e.g. new email).
              </Alert>
            )}

            {/* Composio: browse available actions (read-only reference) */}
            {isComposioTool && selectedTool && (
              <Box className={styles.section}>
                <Typography variant="body2" className={styles.sectionLabel}>
                  Available actions
                </Typography>
                <Box className={styles.composioActionsPanel}>
                  {loadingActions ? (
                    <Box className={styles.composioActionsLoading}>
                      <CircularProgress size={18} />
                      <Typography variant="caption" color="text.secondary">
                        Loading actions…
                      </Typography>
                    </Box>
                  ) : actions.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No actions returned for this toolkit. You can still connect — the agent may use Composio&apos;s live
                      tool catalog at runtime.
                    </Typography>
                  ) : (
                    <Box className={styles.composioActionChips}>
                      {actions.map((action) => {
                        const actionName = action.name || action.action_name || action.id;
                        return (
                          <Chip
                            key={actionName}
                            label={actionName}
                            size="small"
                            variant="outlined"
                            className={styles.composioActionChip}
                            onClick={() => setSelectedActionDetails(action)}
                            title={action.description || actionName}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Action Selection — required for custom tools; MCP uses all actions */}
            {selectedToolType !== 'mcp_server' && !isComposioTool && (
              <Box className={styles.section}>
                <Typography variant="body2" className={styles.sectionLabel}>
                  Action <span className={styles.required}>*</span>
                </Typography>
                <Box className={styles.toolSelectContainer}>
                  <FormControl
                    fullWidth
                    size="small"
                    className={styles.selectField}
                    disabled={!selectedTool}
                    sx={{ position: 'relative' }}
                  >
                    <InputLabel>
                      {(loadingActions || isEditModeLoading) ? "Loading actions..." : "Select Action..."}
                    </InputLabel>
                    <Select
                      value={selectedAction}
                      onChange={handleActionChange}
                      label={(loadingActions || isEditModeLoading) ? "Loading actions..." : "Select Action..."}
                      disabled={loadingActions || isEditModeLoading}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            maxWidth: 350,
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {(() => {
                        const isLoading = loadingActions || isEditModeLoading;
                        return isLoading ? (
                          <MenuItem disabled>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Loading actions...
                          </MenuItem>
                        ) : !selectedTool ? (
                          <MenuItem disabled>
                            Please select a tool first
                          </MenuItem>
                        ) : actions.length === 0 ? (
                          <MenuItem disabled>
                            No actions available
                          </MenuItem>
                        ) : (
                          actions.map((action) => {
                            const actionName = action.name || action.action_name || action.id;
                            const actionDesc = action.description || '';

                            return (
                              <MenuItem key={actionName} value={actionName}>
                                <InfoTooltip
                                  title={actionName}
                                  content={actionDesc || "No description available"}
                                  placement="right"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      width: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {actionName}
                                  </Typography>
                                </InfoTooltip>
                              </MenuItem>
                            );
                          })
                        )
                      })()}
                    </Select>
                    {(loadingActions || isEditModeLoading) && (
                      <Box sx={{
                        position: 'absolute',
                        right: '40px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        pointerEvents: 'none'
                      }}>
                        <CircularProgress size={16} />
                      </Box>
                    )}
                  </FormControl>
                </Box>
              </Box>
            )}

            {/* Parameters Section — from pinned custom action or browsed Composio action chip */}
            {selectedActionDetails && selectedActionDetails.parameters && (
              <Box className={styles.section}>
                <Typography variant="body2" className={styles.sectionLabel}>
                  Parameters
                </Typography>
                <Box className={styles.parametersContainer}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Add parameters in agent instructions or during conversation with agent. (*) indicates mandatory fields.
                  </Typography>

                  {(() => {
                    let paramsToRender = [];
                    const params = selectedActionDetails.parameters;

                    if (Array.isArray(params)) {
                      // Handle Array format (ActionBuilder / Custom Tools)
                      paramsToRender = params.map(p => ({
                        name: p.name,
                        description: p.description,
                        required: p.required,
                        examples: [] // ActionBuilder doesn't usually have examples field yet
                      }));
                    } else if (params && typeof params === 'object') {
                      // Handle JSON Schema format (Composio / Raw MCP)
                      const properties = params.properties || {};
                      const requiredList = Array.isArray(params.required) ? params.required : [];

                      paramsToRender = Object.entries(properties).map(([key, value]) => ({
                        name: key,
                        description: value.description,
                        required: requiredList.includes(key),
                        examples: value.examples || []
                      }));
                    }

                    // Sort: Required first, then alphabetical
                    paramsToRender.sort((a, b) => {
                      if (a.required && !b.required) return -1;
                      if (!a.required && b.required) return 1;
                      return a.name.localeCompare(b.name);
                    });

                    if (paramsToRender.length === 0) {
                      return (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No parameters defined.
                        </Typography>
                      );
                    }

                    return paramsToRender.map((param) => (
                      <Box key={param.name} className={styles.parameterItem}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {param.name}
                          </Typography>
                          {param.required && (
                            <Typography variant="body2" sx={{ color: '#e36250' }}>
                              *
                            </Typography>
                          )}
                        </Box>

                        {param.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.5 }}
                          >
                            {param.description}
                          </Typography>
                        )}

                        {param.examples && param.examples.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              Examples:
                            </Typography>
                            {param.examples.map((example, idx) => (
                              <Typography
                                key={idx}
                                variant="caption"
                                sx={{ display: 'block', color: 'text.secondary', ml: 1 }}
                              >
                                {typeof example === 'string' || typeof example === 'number' || typeof example === 'boolean'
                                  ? String(example)
                                  : JSON.stringify(example)}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ));
                  })()}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button
            onClick={handleClose}
            variant="outlined"
            className={styles.cancelButton}
          >
            Cancel
          </Button>
          <Tooltip title={connectDisabledReason || ''} disableHoverListener={!connectDisabledReason}>
            <span>
              <Button
                variant="contained"
                onClick={handleConnect}
                disabled={connectDisabled}
                className={styles.saveButton}
              >
                {editTool ? 'Update' : isComposioTool && selectedTool ? 'Connect toolkit' : 'Connect'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AddToolDialog;
